import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Button, Card, CardHeader, Typography } from "@material-ui/core";
import { setUserActivties } from "../../auth/setUserActivties";
import BackgroundWrapper from "../base/BackgroundWrapper";
import { Wrapper } from "../base/Wrapper";
import { useVerified } from "../../hooks/useVerified";
import { generateID } from "../../lib/utilities";
import { getCurrentUserInfo } from "../../auth/getCurrentUserInfo";
import { checkBadges } from "../../lib/badgeLogic";
import { getCurrentDateActivity } from "../../auth/getCurrentDateActivity";
import { MdRepeat, MdTimer, MdControlPoint, MdTrendingUp } from "react-icons/md";
import { IoMdArrowDropdown, IoMdArrowDropup } from "react-icons/io";
import { useStationData } from "../../hooks/useStationData";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    height: "100%",
    overflow: "scroll",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    color: theme.palette.common.white,
  },
  titleContainer: {
    marginTop: theme.spacing(7.5),
  },
  text: {
    wordWrap: "break-word",
    hyphens: "auto",
  },
  textMintGreen: {
    color: "#00FEDF",
  },
  textWhite: {
    color: theme.palette.common.white,
  },
  infoCards: {
    margin: theme.spacing(2, 0),
  },
  button: {
    marginBottom: theme.spacing(2),
  },
  icon: {
    fontSize: "165%",
    color: theme.palette.common.white,
  },
  timeIcon: {
    margin: theme.spacing(0.5, 0.5, 0, 0),
    verticalAlign: "bottom",
  },
  infocard: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "transparent",
    marginBottom: theme.spacing(1),
    paddingRight: theme.spacing(2),
    borderColor: theme.palette.common.white,
  },
  primaryCard: {
    borderColor: theme.palette.primary.main,
    borderWidth: "2px",
    boxShadow: `${"inset 0 0 50px"}${theme.palette.primary.main}`,
    paddingRight: theme.spacing(2),
  },
  cardNumber: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    width: 20,
    color: theme.palette.common.white,
  },
}));

function ExerciseSuccess({ exName, navigate, seconds, stNr, repetition }) {
  const classes = useStyles();
  const { isVerified } = useVerified();
  const [currentDayActivity, setCurrentDayActivity] = useState(null);
  const [userActivities] = useStationData(stNr);
  const [highestRepetition, setHighestRepetition] = useState(0);
  const [repetitionState, setRepetitionState] = useState(parseInt(repetition));
  const [userData, setUserData] = useState();

  useEffect(() => {
    const loadUserInfo = async () => {
      const userInfo = await getCurrentUserInfo();
      if (userInfo) {
        setUserData(userInfo);
      }
    };

    loadUserInfo();
  }, []);

  useEffect(() => {
    for (let i in userActivities) {
      if (userActivities.hasOwnProperty(i)) {
        for (let e in userActivities[i].exercises) {
          let repetition = userActivities[i].exercises[e].repetition;
          if (repetition > highestRepetition) {
            setHighestRepetition(repetition);
          }
        }
      }
    }
  }, [highestRepetition, userActivities]);

  useEffect(() => {
    const fetchAllActivities = async () => {
      if (!currentDayActivity) {
        const activities = await getCurrentDateActivity();
        setCurrentDayActivity(activities);
      }
    };
    fetchAllActivities();
  }, [currentDayActivity]);

  const handleSuccess = async () => {
    await setUserActivties(userActivity);
    const badgeData = await checkBadges(
      userData,
      isVerified,
      seconds,
      currentDayActivity,
      userActivity
    );
    navigate("../../", { state: { update: true, badgeData } });
  };

  const userActivity = {
    duration: seconds,
    name: exName,
    verified: isVerified,
    repetition: repetitionState,
    stNr,
    id: generateID(),
  };

  function InfoCard({ icon, message, subtitle, number, primary }) {
    const Icon = icon;
    return (
      <Card variant="outlined" className={`${classes.infocard} ${primary && classes.primaryCard}`}>
        <CardHeader
          avatar={<Icon className={classes.icon} />}
          title={<Typography className={classes.textWhite}>{message}</Typography>}
          subheader={
            <Typography variant="subtitle2" className={classes.textWhite}>
              {subtitle}
            </Typography>
          }
        />
        {number &&
          (message !== "Wiederholungen" ? (
            <Typography variant="h5" component="body1" className={classes.cardNumber}>
              {number}
            </Typography>
          ) : (
            <Typography variant="h5" component="body1" className={classes.cardNumber}>
              <IoMdArrowDropup
                onClick={(e) => {
                  setRepetitionState((prevState) => ++prevState);
                }}
              />
              {repetitionState}
              <IoMdArrowDropdown
                onClick={
                  repetitionState > 1 ? (e) => setRepetitionState((prevState) => --prevState) : null
                }
                style={repetitionState <= 1 && { visibility: "hidden" }}
              />
            </Typography>
          ))}
      </Card>
    );
  }

  return (
    <BackgroundWrapper blurValue={4}>
      <Wrapper vh100>
        <div className={classes.root}>
          <div className={classes.titleContainer}>
            <Typography
              variant="h6"
              className={`${classes.text} ${classes.textMintGreen}`}
              align="center"
              gutterBottom
            >
              Geschafft!
            </Typography>
            <Typography
              variant="h2"
              className={`${classes.text} ${classes.textWhite}`}
              align="center"
            >
              <strong>{exName}</strong>
            </Typography>
            <div align="center">
              <MdTimer display="inline" className={`${classes.icon} ${classes.timeIcon}`} />
              <Typography variant="subtitle1" display="inline">
                {new Date(seconds * 1000).toISOString().substr(14, 5)}
              </Typography>
            </div>
          </div>
          <div className={classes.infoCards}>
            {highestRepetition < repetitionState && (
              <InfoCard
                icon={MdTrendingUp}
                message={"neue Bestleistung"}
                subtitle={`${"erzielt "}${"(+"}${repetitionState - highestRepetition}${" Wdh.)"}`}
                primary
              />
            )}
            <InfoCard
              icon={MdRepeat}
              message="Wiederholungen"
              subtitle="ausgeübt"
              number={repetition}
            />
            {seconds / 60 >= 1 && (
              <InfoCard
                icon={MdControlPoint}
                message={`${seconds / 60 >= 2 ? " Punkte" : " Punkt"}`}
                subtitle="erhalten"
                number={Math.floor(seconds / 60)}
              />
            )}
          </div>
          <Button
            variant="contained"
            color="primary"
            fullWidth={true}
            className={classes.button}
            onClick={() => handleSuccess()}
          >
            Abschließen
          </Button>
        </div>
      </Wrapper>
    </BackgroundWrapper>
  );
}

export default ExerciseSuccess;
